import type { FleetPortalSection, PortalSectionType } from '@breaker/shared-types';

/**
 * Client-side mirror of the server resolver's defaults
 * (apps/api/src/modules/site/resolver.ts — keep the copy in step; the
 * server is the truth, this exists so the preview updates on every
 * KEYSTROKE with zero round-trips). Same inputs, same defaults.
 */

export interface PreviewFleetInfo {
  name: string;
  phone: string | null;
  email: string | null;
  address: string | null;
  /** Highlight labels derived server-side from the rate card; the GET
   *  hands them over so the client never re-implements tariff logic. */
  defaultHighlights: string[];
}

export const HIGHLIGHT_LABELS: Record<string, string> = {
  same_day: 'Same-day delivery',
  multi_drop: 'Multi-drop routes',
  fragile: 'Fragile & high-care',
  out_of_hours: 'Out-of-hours',
  nationwide: 'Nationwide coverage',
  refrigerated: 'Refrigerated',
  pallets: 'Pallet work',
  two_man: 'Two-man crews',
};

export function resolvePreviewSection(
  fleet: PreviewFleetInfo,
  type: PortalSectionType,
  raw: Record<string, unknown>,
): Record<string, unknown> {
  switch (type) {
    case 'hero': {
      const c = raw as { headline?: string; subline?: string; imageUrl?: string };
      return {
        headline: c.headline?.trim() || fleet.name,
        subline:
          c.subline?.trim() || 'Customer portal — book, track, and download invoices.',
        imageUrl: c.imageUrl ?? null,
      };
    }
    case 'about': {
      const c = raw as { heading?: string; body?: string; photoUrl?: string };
      return {
        heading: c.heading?.trim() || `About ${fleet.name}`,
        body:
          c.body?.trim() ||
          `${fleet.name} moves freight for businesses across the UK. Book online and track every job live.`,
        photoUrl: c.photoUrl ?? null,
      };
    }
    case 'highlights': {
      const c = raw as { items?: Array<{ kind: string; value: string }> };
      const items =
        c.items && c.items.length >= 3
          ? c.items.map((i) => (i.kind === 'preset' ? HIGHLIGHT_LABELS[i.value] ?? i.value : i.value))
          : fleet.defaultHighlights;
      return { items };
    }
    case 'gallery': {
      const c = raw as { images?: Array<{ url: string; caption?: string }> };
      return {
        images: (c.images ?? []).map((i) => ({ url: i.url, caption: i.caption ?? null })),
      };
    }
    case 'contact': {
      const c = raw as {
        overrides?: Record<string, string>;
        show?: Record<string, boolean>;
      };
      const resolved = {
        phone: c.overrides?.['phone'] ?? fleet.phone,
        email: c.overrides?.['email'] ?? fleet.email,
        address: c.overrides?.['address'] ?? fleet.address,
        openingHours: c.overrides?.['openingHours'] ?? null,
      };
      return {
        ...resolved,
        show: {
          phone: (c.show?.['phone'] ?? true) && resolved.phone !== null,
          email: (c.show?.['email'] ?? true) && resolved.email !== null,
          address: (c.show?.['address'] ?? true) && resolved.address !== null,
          openingHours:
            (c.show?.['openingHours'] ?? true) && resolved.openingHours !== null,
        },
      };
    }
    case 'faq': {
      const c = raw as { items?: unknown[] };
      return { items: c.items ?? [] };
    }
    case 'coverage': {
      const c = raw as { areas?: string[] };
      // Empty counts as unset — mirrors the server resolver.
      return { areas: c.areas?.length ? c.areas : ['Nationwide'] };
    }
    case 'text': {
      const c = raw as { doc?: unknown };
      return { doc: c.doc ?? { type: 'doc', content: [{ type: 'paragraph', content: [] }] } };
    }
    case 'cta': {
      const c = raw as { line?: string };
      return { line: c.line?.trim() || 'Ready to move something?' };
    }
    default:
      return raw;
  }
}

export const DEFAULT_HOME_TYPES: PortalSectionType[] = [
  'hero',
  'about',
  'highlights',
  'contact',
];

/** Sections for the preview: stored+enabled resolved, or the default
 *  Home composition when the (system) page has none. */
export function resolvePreviewSections(
  fleet: PreviewFleetInfo,
  isSystemPage: boolean,
  sections: Array<Pick<FleetPortalSection, 'type' | 'enabled' | 'content'>>,
): Array<{ type: PortalSectionType; content: Record<string, unknown> }> {
  const enabled = sections.filter((s) => s.enabled);
  if (isSystemPage && enabled.length === 0) {
    return DEFAULT_HOME_TYPES.map((type) => ({
      type,
      content: resolvePreviewSection(fleet, type, {}),
    }));
  }
  return enabled.map((s) => ({
    type: s.type,
    content: resolvePreviewSection(fleet, s.type, s.content),
  }));
}
